import {
  AfterViewInit,
  Directive,
  ElementRef,
  EventEmitter,
  Host,
  HostBinding,
  HostListener,
  Input,
  OnDestroy,
  Output,
} from '@angular/core';
import {
  ControlContainer,
  UntypedFormControl,
  NgForm,
  ValidatorFn,
  Validators,
} from '@angular/forms';

/**
 * Gets the onChange event of the patternlib-textinput and other form elements
 * and writes the value into the angular forms value dictionary.
 */
@Directive({
  selector: '[ngWebComponentModel]',
  exportAs: 'ngWebComponentModel',
})
export class WebComponentFormElementDirective implements AfterViewInit, OnDestroy
{
  private name: string | null = null;
  public control: UntypedFormControl | null = null;
  private form: NgForm | null = null;

  @Input()
  required: string | boolean |  null = null;

  @Input()
  showSuccess = false;

  @Input()
  validateOnSubmitted = true;

  @Input()
  customValidation: ValidatorFn | null = null;

  @Input('selected')
  @Input('value')
  set valueAttribute(val: any) {
    this.componentModel = val;
  }

  @Input('ngWebComponentModel')
  set componentModel(val: any) {
    this.model = val;
    this.control?.setValue(this.model);
    this.control?.markAsDirty();
  }

  @HostBinding('value')
  @HostBinding('selected')
  model: any;

  @Output('ngWebComponentModelChange')
  update = new EventEmitter<any>();

  constructor(private el: ElementRef, @Host() parent: ControlContainer) {
    if (!(parent instanceof NgForm))
      throw Error(
        '[webcomponent-form-element] must only be used inside a NgForm container'
      );

    this.form = parent;
  }

  ngOnDestroy(): void {
    this.form && this.form.form.removeControl(this.name!);
  }

  ngAfterViewInit() {
    setTimeout(() => {
      this.name = this.el.nativeElement.getAttribute('name');
      const validators = [
        this.required !== null && ((typeof this.required === 'string' && this.required !== 'false') || (typeof this.required === 'boolean' && this.required === true))
          ? Validators.required
          : null,
        this.customValidation,
      ];
      this.control = new UntypedFormControl(
        null,
        validators.filter(x => x! != null).map(x => x!)
      );
      this.form!.form.addControl(this.name!, this.control);

      const value = this.el?.nativeElement?.value;
      if (value) {
        this.control.setValue(value);
      }

      if (this.model) {
        this.control.setValue(this.model);
        this.control?.markAsDirty();
      }
    }, 0);
  }

  @HostBinding('attr.status')
  get getStatus() {
    if (this.form?.submitted || !this.validateOnSubmitted) {
      if (this.invalid) return 'error';
      if (this.showSuccess && this.valid) return 'success';
    }
    return 'default';
  }

  @HostListener('lhBlur')
  @HostListener('onBlur')
  onBlur() {
    this.control?.markAsTouched();
  }

  @HostListener('lhSelect', ['$event'])
  @HostListener('lhSearchChange', ['$event'])
  @HostListener('lhChange', ['$event'])
  @HostListener('onSelect', ['$event'])
  @HostListener('onSearchChange', ['$event'])
  @HostListener('onChange', ['$event'])
  onChange(evt: any) {
    this.control?.markAsDirty();
    this.control?.markAsTouched();

    let emitValue = evt.detail.value;
    if (
      evt.detail != null &&
      typeof evt.detail === 'object' &&
      'selected' in evt.detail
    ) {
      // Handle dropdown edge case.
      emitValue = evt.detail.selected.value;
    }

    this.control?.setValue(emitValue);
    this.update.emit(emitValue);
  }

  // Allow direct access to required properties of the control
  get errors() {
    return this.control?.errors;
  }

  get dirty() {
    return this.control?.dirty;
  }

  get touched() {
    return this.control?.touched;
  }

  get status() {
    return this.control?.status;
  }

  get valid() {
    return this.control?.valid;
  }

  get invalid() {
    return this.control?.invalid;
  }

  get value() {
    return this.control?.value;
  }
}
